import * as React from 'react'

import { EntitySelect, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

interface TreeRole {
  id: number
  uuid: string
  name?: string | null
  ancestor?: {
    id: number
    name?: string | null
    uuid: string
  } | null
}

interface JobCatalogRoleSelectorProps {
  isLoading: boolean
  onChange: (uuid: string | null) => void
  treeRoles: TreeRole[] | undefined
  value?: string
}

const JobCatalogRoleSelector = ({
  isLoading,
  onChange,
  treeRoles = [],
  value,
}: JobCatalogRoleSelectorProps) => {
  const roleEntities = React.useMemo<EntitySelectEntity[]>(() => {
    if (treeRoles.length === 0) {
      return []
    }

    const ancestorMap = new Map<
      string,
      { id: string; name: string; roles: Array<{ uuid: string; name: string | null }> }
    >()

    treeRoles.forEach((role) => {
      const ancestorKey = role.ancestor?.id ? String(role.ancestor.id) : 'ungrouped'
      const ancestorName = role.ancestor?.name ?? ''

      if (!ancestorMap.has(ancestorKey)) {
        ancestorMap.set(ancestorKey, {
          id: ancestorKey,
          name: ancestorName,
          roles: [],
        })
      }
      const ancestorGroup = ancestorMap.get(ancestorKey)
      if (ancestorGroup) {
        ancestorGroup.roles.push({
          uuid: role.uuid,
          name: role.name ?? null,
        })
      }
    })

    return Array.from(ancestorMap.values()).map((ancestor) => ({
      id: ancestor.id,
      name: ancestor.name,
      expanded: true,
      subItems: ancestor.roles.map((role) => ({
        subId: role.uuid,
        subName: role.name ?? '',
      })),
    }))
  }, [treeRoles])

  const selectedRoleEntity = React.useMemo<EntitySelectEntity | undefined>(() => {
    if (!value) return undefined

    for (const entity of roleEntities) {
      const matchingSubItem = entity.subItems?.find((subItem) => subItem.subId === value)
      if (matchingSubItem) {
        return {
          ...entity,
          subItems: [matchingSubItem],
        }
      }
    }
    return undefined
  }, [roleEntities, value])

  const handleRoleSelect = React.useCallback(
    (selectedEntity: EntitySelectEntity | null) => {
      const subItem = selectedEntity?.subItems?.[0]
      if (!subItem) {
        onChange(null)
        return
      }
      onChange(String(subItem.subId))
    },
    [onChange]
  )

  if (treeRoles.length === 0) {
    return null
  }

  return (
    <EntitySelect
      width={360}
      label={i18n.t('workflows.sidepanel.approval.strategies.selectors.roles')}
      hideLabel
      loading={isLoading}
      searchPlaceholder={i18n.t(
        'workflows.sidepanel.approval.strategies.selectors.roles_search_placeholder'
      )}
      notFoundTitle={i18n.t(
        'workflows.sidepanel.approval.strategies.selectors.roles_not_found_title'
      )}
      notFoundSubtitle={i18n.t(
        'workflows.sidepanel.approval.strategies.selectors.roles_not_found_subtitle'
      )}
      entities={roleEntities}
      groups={[]}
      selectedGroup="all"
      onSelect={handleRoleSelect}
      onItemExpandedChange={() => {}}
      onGroupChange={() => {}}
      selectedEntities={selectedRoleEntity ? [selectedRoleEntity] : []}
      selectedItemsCopy={i18n.t(
        'workflows.sidepanel.approval.strategies.selectors.roles_selected_copy'
      )}
      placeholder={i18n.t('workflows.sidepanel.approval.strategies.selectors.roles_placeholder')}
      singleSelector
      hiddenAvatar
      size="md"
    />
  )
}

export default JobCatalogRoleSelector
